// src/components/admin/CsvImporter.tsx
import React, { useState, useRef } from 'react';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  addDoc,
  serverTimestamp, 
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../../firebase';
import Papa from 'papaparse';

interface StudentData {
  'Student Name': string;
  'Email Address': string;
  'Password': string;
  'UID': string;
}

const CsvImporter: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<{success: number, failed: number, messages: string[]}>({
    success: 0,
    failed: 0,
    messages: []
  });
  const [status, setStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setStatus('idle');
      setResults({success: 0, failed: 0, messages: []});
    }
  };

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    setStatus('processing');
    
    try {
      // Parse CSV
      const text = await file.text();
      Papa.parse<StudentData>(text, {
        header: true,
        skipEmptyLines: true,
        complete: async (results: { data: any[]; }) => {
          const successCount = await processStudents(results.data);
          setResults({
            success: successCount,
            failed: results.data.length - successCount,
            messages: results.data.map((d: { [x: string]: any; }) => 
              `${d['Student Name']} (${d['Email Address']}): ${d['UID'] ? 'Has UID' : 'Missing UID'}`
            )
          });
          setStatus(successCount > 0 ? 'success' : 'error');
        },
        error: (error: any) => {
          console.error('Error parsing CSV:', error);
          setResults({
            success: 0,
            failed: 0,
            messages: ['Error parsing CSV file']
          });
          setStatus('error');
        }
      });
    } catch (err) {
      console.error('Error importing students:', err);
      setResults({
        success: 0,
        failed: 0,
        messages: [(err as Error).message]
      });
      setStatus('error');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const processStudents = async (students: StudentData[]): Promise<number> => {
    let successCount = 0;
    
    for (const student of students) {
      try {
        // Validate required fields
        if (!student['Student Name'] || !student['Email Address'] || !student['UID']) {
          console.warn('Skipping student with missing data:', student);
          continue;
        }
        
        // Check if a user doc already exists with this UID
        const userRef = doc(db, 'users', student['UID']);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          // Create user in users collection
          await setDoc(userRef, {
            uid: student['UID'],
            email: student['Email Address'],
            displayName: student['Student Name'],
            password: student['Password'] || 'password123', // Default password if missing
            role: 'student',
            createdAt: serverTimestamp()
          });
          
          // Check if user already has a form
          const formsRef = collection(db, 'pcrForms');
          const q = query(formsRef, where("userId", "==", student['UID']));
          const querySnapshot = await getDocs(q);
          
          if (querySnapshot.empty) {
            // Create PCR form
            await addDoc(collection(db, 'pcrForms'), {
              userId: student['UID'],
              studentName: student['Student Name'],
              timestamp: serverTimestamp(),
              lastSaved: serverTimestamp(),
              dispatchScenario: `At 19:41 you are dispatched to the scene of a winery in the River North neighborhood for "Altered Mental Status". Your patient is a 48 y/o female who is found sitting in a chair being tended to by the bar staff. The first thing you notice is that she is morbidly obese. As you approach, a man who states that he "called 911" and that this is "only their second date". The man then explains that they had just arrived at the restaurant and ordered a glass of wine at the standing bar (no food), while waiting to be seated. He tells you that (prior to your arrival) he had excused himself to the restroom, and when he returned, he found his date was standing there and her glass of wine was shattered on the floor. "All of a sudden she became disoriented."
You begin your assessment and she is able to tell you her name and location, but she is aphasic. You also notice that your PT has an insulin pump clipped to her belt, and your partner finds Metoprolol in her purse. When asked, the PT complains of "dizziness" and "nausea". No medic alert bracelet found. Initial vital signs are: BP 160/110, HR 134, RR 24 (shallow), skin is cool, pale, and diaphoretic, both pupils appear to have a gaze.`,
              // Add empty form structure
              sceneSizeUp: {
                BSI: '',
                natureOfIllness: '',
                additionalResources: '',
                spinalPrecautions: ''
              },
              primaryAssessment: {
                generalImpression: '',
                LOCMentalStatus: {
                  AVPU: '',
                  orientation: []
                },
                chiefComplaint: '',
                airwayAssessment: '',
                breathingAssessment: '',
                circulationAssessment: {
                  skin: {
                    color: '',
                    temperature: '',
                    condition: ''
                  },
                  pulse: {
                    rate: '',
                    rhythm: '',
                    quality: ''
                  }
                },
                GCS: {
                  eyeOpening: 0,
                  verbalResponse: 0,
                  motorResponse: 0,
                  totalScore: 0
                },
                patientPriority: ''
              },
              differentialDiagnosis: {
                pathway1: '',
                pathway2: '',
                pathway3: ''
              },
              // Add other required fields with empty values
              vitalSigns: {
                sets: [
                  {
                    time: '',
                    bloodPressure: '',
                    heartRate: '',
                    respiratoryRate: '',
                    spO2: '',
                    BGL: '',
                    IVGauge: null,
                    EKG: {
                      reading: '',
                      leadType: ''
                    },
                    pupils: [],
                    lungSounds: []
                  },
                  {
                    time: '',
                    bloodPressure: '',
                    heartRate: '',
                    respiratoryRate: '',
                    spO2: '',
                    BGL: '',
                    IVGauge: null,
                    EKG: {
                      reading: '',
                      leadType: ''
                    },
                    pupils: [],
                    lungSounds: []
                  },
                  {
                    time: '',
                    bloodPressure: '',
                    heartRate: '',
                    respiratoryRate: '',
                    spO2: '',
                    BGL: '',
                    IVGauge: null,
                    EKG: {
                      reading: '',
                      leadType: ''
                    },
                    pupils: [],
                    lungSounds: []
                  }
                ],
                reassessment: ''
              },
              pathophysiology: {
                pathway1: '',
                pathway2: '',
                pathway3: ''
              },
              fieldDiagnosis: {
                step1: '',
                step2: '',
                step3: '',
                treatmentAndInterventions: ''
              },
              documentation: {
                narrative: '',
                radioReport: '',
                drugCards: '',
                formatAndResources: ''
              }
            });
          }
          
          successCount++;
        } else {
          // User already exists
          console.log(`User ${student['Student Name']} already exists in the database.`);
          // Count as success since we're not trying to overwrite
          successCount++;
        }
      } catch (err) {
        console.error(`Error processing student ${student['Student Name']}:`, err);
      }
    }
    
    return successCount;
  };

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Import Students from CSV</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select CSV file with student data
          </label>
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
          <p className="mt-1 text-xs text-gray-500">
            CSV must include columns: "Student Name", "Email Address", "Password", "UID"
          </p>
        </div>
        
        <div className="flex justify-between items-center">
          <button
            onClick={handleImport}
            disabled={!file || importing}
            className={`${
              !file || importing 
                ? 'bg-gray-300 cursor-not-allowed' 
                : 'bg-indigo-600 hover:bg-indigo-700'
            } text-white py-2 px-4 rounded-md transition-colors`}
          >
            {importing ? 'Importing...' : 'Import Students'}
          </button>
          
          {status !== 'idle' && (
            <span className={`text-sm font-medium ${
              status === 'processing' ? 'text-yellow-600' :
              status === 'success' ? 'text-green-600' : 'text-red-600'
            }`}>
              {status === 'processing' ? 'Processing...' :
               status === 'success' ? 'Import successful!' : 'Import failed'}
            </span>
          )}
        </div>
      </div>
      
      {(status === 'success' || status === 'error') && (
        <div className={`px-6 py-4 border-t ${
          status === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
        }`}>
          <h3 className="text-sm font-semibold mb-2">Import Results</h3>
          <div className="flex space-x-4 mb-2">
            <div className="text-green-700">
              <span className="font-bold">{results.success}</span> successful
            </div>
            <div className="text-red-700">
              <span className="font-bold">{results.failed}</span> failed
            </div>
          </div>
          
          {results.messages.length > 0 && (
            <div className="mt-2">
              <details>
                <summary className="cursor-pointer text-sm font-medium">View Details</summary>
                <div className="mt-2 bg-white p-2 rounded border text-xs font-mono h-32 overflow-y-auto">
                  {results.messages.map((msg, i) => (
                    <div key={i} className="py-1">{msg}</div>
                  ))}
                </div>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CsvImporter;